import { DateValue, parseDate } from '@internationalized/date';
import {
  enumPurchaseOrderSortOption,
  PurchaseOrderSortOption,
  PurchaseOrderStatus,
} from '@rematter/frontend-lib-api-client';
import { DataGridSelection } from '@rematter/ui-web';
import { atom } from 'jotai';

import { purchaseOrderDeleteAtom } from '../_graphql/mutationPurchaseOrderDelete';
import { purchaseOrderUpdateAtom } from '../_graphql/mutationPurchaseOrderUpdate';
import { purchaseOrdersListQueryAtom } from '../_graphql/queryPurchaseOrdersList';
import { PurchaseOrderRecord } from '../types/purchaseOrder';

import { atomFromQueryString } from '~/shared/state';
import { anyDateToISOString } from '~/shared/utils/dateTime';

// 🟣🟣🟣 PARAMS
export const $searchTerm = atomFromQueryString.string('q');
export const $selectedFacility = atomFromQueryString.string('f');
export const $limit = atomFromQueryString.number('limit', 25);
export const $offset = atomFromQueryString.number('offset', 0);
export const $selectedSupplierIds =
  atomFromQueryString.stringSet('supplierIds');
export const $selectedCreatedByUserIds =
  atomFromQueryString.stringSet('createdByUserIds');

// 🟣🟣🟣 COLUMN FILTER PARAMS
export const $columnDateRange = atomFromQueryString.dateRange();
export const $columnCreatedOnDateRange = atomFromQueryString.custom<{
  start: DateValue;
  end: DateValue;
} | null>('createdOnRange', {
  parse: (value) => {
    if (!value) return null;
    try {
      const [startStr, endStr] = value.split('..');
      if (!startStr || !endStr) return null;

      const start = parseDate(startStr);
      const end = parseDate(endStr);

      return { start, end };
    } catch {
      return null;
    }
  },
  serialize: (value) => {
    if (!value || !value.start || !value.end) return undefined;

    return `${value.start.toString()}..${value.end.toString()}`;
  },
  defaultValue: undefined,
});
export const $columnSelectedStatuses =
  atomFromQueryString.stringSet<PurchaseOrderStatus>('colStatuses');

// 🔘🔘🔘 PLAIN ATOMS
export const $selectedKeys = atom<DataGridSelection>(new Set<React.Key>());
export const $isWizardOpen = atom<boolean>(false);
export const $draftPurchaseOrderId = atom<string | null>(null);
export const $selectedPurchaseOrder = atom<PurchaseOrderRecord | null>(null);
export const $isVoidDialogOpen = atom<boolean>(false);
export const $isCloseDialogOpen = atom<boolean>(false);
export const $isLoadDialogOpen = atom<boolean>(false);
export const $currentSorting = atom<PurchaseOrderSortOption[]>([
  enumPurchaseOrderSortOption.CreatedAtDesc,
]);

// 🟢🟢🟢 REMOTE DATA
export const $remotePurchaseOrders = purchaseOrdersListQueryAtom((get) => {
  const searchTerm = get($searchTerm);
  const selectedFacility = get($selectedFacility);
  const limit = get($limit);
  const offset = get($offset);
  const accountIds = Array.from(get($selectedSupplierIds)).map((id) =>
    id.toString(),
  );

  const columnSelectedStatuses = get($columnSelectedStatuses);
  const columnDateRange = get($columnDateRange);
  const sortingParams = get($currentSorting);

  const params: {
    searchTerm?: string;
    facilityId?: string;
    limit: number;
    offset: number;
    sorting: PurchaseOrderSortOption[];
    includeLineItems?: boolean;
    accountIds?: string[];
    status?: PurchaseOrderStatus[];
    fromEndDate?: string;
    toEndDate?: string;
  } = {
    limit,
    offset,
    sorting: sortingParams,
    includeLineItems: true,
    accountIds,
  };

  if (searchTerm) params.searchTerm = searchTerm;
  if (selectedFacility && selectedFacility !== 'all')
    params.facilityId = selectedFacility;

  if (columnSelectedStatuses.size > 0) {
    params.status = Array.from(columnSelectedStatuses);
  }

  if (columnDateRange?.start) {
    params.fromEndDate = anyDateToISOString(columnDateRange.start);
  }
  if (columnDateRange?.end) {
    params.toEndDate = anyDateToISOString(columnDateRange.end);
  }

  return {
    variables: params,
  };
});

// 🟠🟠🟠 ACTION -> DELETE PURCHASE ORDER
export const $deletePurchaseOrder = purchaseOrderDeleteAtom({
  onSuccess: (get) => {
    get($remotePurchaseOrders).refetch?.();
  },
});

// 🟠🟠🟠 ACTION -> UPDATE PURCHASE ORDER (for bulk operations)
export const $updatePurchaseOrder = purchaseOrderUpdateAtom({
  onSuccess: (get) => {
    get($remotePurchaseOrders).refetch?.();
  },
});
